import { keywords } from '../../parser/keywords.js';
import { ClassInstanceField, Test, TestType } from '../../parser/models/all.js';
import { hasOnlyDecorator } from '../../parser/utils/testOnlyUtils.js';
import { hasTitleDecorator } from '../../parser/utils/testTitleUtils.js';
import { PreProcessContext } from '../models/preProcessContext.js';

/**
 * Processor for identifying and creating tests from class instance methods.
 * It handles method decorators (only, title, cases) and distinguishes built-in hooks.
 * 
 * @param field - The class instance field containing the method.
 * @param index - Field index in the collection.
 * @param context - The pre-processing context.
 * @returns True if the field was processed as a class instance method.
 */
export function processClassInstFunction(field: ClassInstanceField, index: number, context: PreProcessContext): boolean {
  const { name, value } = field;

  if (field instanceof ClassInstanceField && field.isFunction) {
    const isClassInstBuiltInFunction = context.isBuiltInFunction(name);

    const isClassInstFunctionWithOnly = isClassInstBuiltInFunction === false
      && hasOnlyDecorator(field.value);

    const isClassInstFunctionWithCases = isClassInstBuiltInFunction === false
      && field.hasCasesDecorator;

    const isClassInstFunctionWithTitle = hasTitleDecorator(field.value);

    const onlyExpression = isClassInstFunctionWithOnly
      ? value[keywords.only]
      : ""

    const testCases = isClassInstFunctionWithCases
      ? value[keywords.cases].slice().reverse()
      : [];

    let type = TestType.CLASS_INST_FUNCTION;
    type |= isClassInstBuiltInFunction ? TestType.BUILTIN_FUNCTION : 0;
    type |= isClassInstFunctionWithOnly ? TestType.FUNCTION_WITH_ONLY : 0;
    type |= isClassInstFunctionWithCases ? TestType.FUNCTION_WITH_CASES : 0;
    type |= isClassInstFunctionWithTitle ? TestType.FUNCTION_WITH_TITLE : 0;

    context.tests.push(
      new Test(
        field.groupName,
        // name or title
        isClassInstFunctionWithTitle
          ? value[keywords.title]
          : name,
        // value
        value.bind(field.classInstance),
        // type
        new TestType(type),
        // test cases
        testCases,
        // only expression
        onlyExpression
      )
    );

    return true;
  }

  return false;
}
